// =============================================================================
// Child theme entry — Amazing Floral Gifts interactions.
// Block-based templates: hooks are class-based since core blocks only accept
// className (no data-* attributes). Mobile menu is the wp:navigation block's
// own overlay panel, so no drawer init here.
// =============================================================================

import Swiper from "swiper";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
// Fade effect CSS carries the pointer-events rules that keep invisible
// stacked slides from eating clicks meant for the active slide's buttons.
import "swiper/css/effect-fade";

// ---- Toast ------------------------------------------------------------------
let toastTimer = null;

export function azShowToast(message) {
	const el = document.getElementById("toast");
	if (!el) return;
	el.textContent = message;
	el.classList.add("show");
	clearTimeout(toastTimer);
	toastTimer = setTimeout(() => el.classList.remove("show"), 2600);
}
window.azShowToast = azShowToast;

// ---- Rewards modal -----------------------------------------------------------
// Triggers: legacy [data-az-modal-open] plus .az-open-modal (core button
// block — the class lands on the .wp-block-button wrapper).
function initModal() {
	const backdrop = document.getElementById("modalBackdrop");
	if (!backdrop) return;

	const setOpen = (open) => backdrop.classList.toggle("open", open);

	document.querySelectorAll("[data-az-modal-open], .az-open-modal").forEach((el) =>
		el.addEventListener("click", (e) => {
			e.preventDefault();
			setOpen(true);
		}),
	);
	backdrop.querySelectorAll("[data-az-modal-close]").forEach((btn) =>
		btn.addEventListener("click", () => setOpen(false)),
	);
	backdrop.addEventListener("click", (e) => {
		if (e.target === backdrop) setOpen(false);
	});

	const form = document.getElementById("azRewardsForm");
	form?.addEventListener("submit", (e) => {
		e.preventDefault();
		setOpen(false);
		form.reset();
		azShowToast("Welcome to Amazing Rewards — you're on the list!");
	});
}

// ---- Membership sign-in chooser popups ----------------------------------------
// inc/az-member-popup.php prints the dialog (home / checkout variant) for
// guests only; the homepage variant opens once per browser from a choice
// record, while the checkout variant re-opens on every visit until the
// visitor signs in or registers. Markup + palette live in _member-popup.scss.
const MP_STORE_KEY = "az_member_popup_v1";
const MP_DECIDED = ["new", "member", "guest"];

function mpRead() {
	try {
		const raw = JSON.parse(localStorage.getItem(MP_STORE_KEY) || "{}");
		return { choice: raw.choice || "", home: !!raw.home };
	} catch {
		return { choice: "", home: false };
	}
}

function mpSave(patch) {
	try {
		localStorage.setItem(MP_STORE_KEY, JSON.stringify({ ...mpRead(), ...patch }));
	} catch {
		/* storage unavailable (private mode) — popups simply re-appear */
	}
}

function initMemberPopup() {
	const root = document.querySelector(".mp");
	if (!root) return;

	const state = mpRead();

	const setOpen = (open) => {
		document.body.style.overflow = open ? "hidden" : "";
		if (open) {
			root.hidden = false;
			requestAnimationFrame(() => root.classList.add("is-open"));
			root.querySelector(".mp__close")?.focus();
		} else {
			root.classList.remove("is-open");
			setTimeout(() => {
				root.hidden = true;
			}, 260);
		}
	};

	root.querySelectorAll("[data-mp-choice]").forEach((el) =>
		el.addEventListener("click", () => {
			const choice = el.dataset.mpChoice;
			mpSave({ choice, home: true });
			// "new" / "member" are anchors — let the browser navigate.
			if (choice === "guest" || choice === "skip") setOpen(false);
		}),
	);

	const dismiss = () => {
		mpSave({ home: true });
		setOpen(false);
	};
	root.querySelectorAll("[data-mp-close]").forEach((el) => el.addEventListener("click", dismiss));
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && !root.hidden) dismiss();
	});

	// Homepage: greet once per browser, and never again once an account path
	// was chosen. Checkout: gate every guest visit — it re-opens each time
	// until they sign in or register (server skips logged-in users).
	if (root.dataset.mpVariant === "home") {
		if (MP_DECIDED.includes(state.choice)) return;
		if (state.home) return; // greeted this browser before
		mpSave({ home: true });
	}
	setOpen(true);
}

// ---- Campaign tabs (seasonal page) --------------------------------------------
// Tabs are wp:button blocks with .az-camptab--{key}; panels are wp:columns
// with .az-campanel--{key}. JS swaps .is-active / .is-visible classes.
function initCampaignTabs() {
	const tabs = document.querySelectorAll(".az-camptab");
	if (!tabs.length) return;

	const keyOf = (el) => {
		const cls = [...el.classList].find((c) => c.startsWith("az-camptab--"));
		return cls ? cls.replace("az-camptab--", "") : "";
	};

	tabs.forEach((wrap) => {
		const trigger = wrap.matches("button") ? wrap : wrap.querySelector("button");
		trigger?.addEventListener("click", () => {
			const key = keyOf(wrap);
			tabs.forEach((t) => t.classList.toggle("is-active", t === wrap));
			document.querySelectorAll(".az-campanel").forEach((panel) =>
				panel.classList.toggle("is-visible", panel.classList.contains(`az-campanel--${key}`)),
			);
		});
	});
}

// ---- GrabFood external-order notice (fruits page) -----------------------------
function initGrabfood() {
	document.querySelectorAll(".az-grabfood").forEach((el) =>
		el.addEventListener("click", () =>
			azShowToast("This button links out to GrabFood (external ordering)"),
		),
	);
}

// ---- Hero slider (Swiper) ----------------------------------------------------
// Home hero block emits .hero-swiper with pagination dots; wire them through
// Swiper's pagination module.
function initHeroSlider() {
	document.querySelectorAll(".hero-swiper.swiper").forEach((el) => {
		new Swiper(el, {
			// Swiper 14: imported modules must be registered explicitly.
			modules: [Pagination, Autoplay, EffectFade],
			effect: "fade",
			// crossFade keeps the outgoing slide transparent while the next
			// one fades in (otherwise they blend through each other).
			fadeEffect: { crossFade: true },
			loop: true,
			autoplay: { delay: 5000, disableOnInteraction: false },
			pagination: {
				el: el.querySelector(".hero-dots"),
				clickable: true,
			},
		});
	});
}

// ---- Mobile drawer (header wp:navigation overlay) -----------------------------
// Core's open button only ever opens — make it a toggle (it morphs into the
// × via CSS) and let taps on the dimmed backdrop close the drawer.
function initDrawer() {
	document.querySelectorAll(".site-nav .wp-block-navigation").forEach((nav) => {
		const container = nav.querySelector(".wp-block-navigation__responsive-container");
		const openBtn = nav.querySelector(".wp-block-navigation__responsive-container-open");
		const closeBtn = nav.querySelector(".wp-block-navigation__responsive-container-close");
		if (!container || !openBtn || !closeBtn) return;

		openBtn.addEventListener("click", () => {
			if (!container.classList.contains("is-menu-open")) return;
			// Core's own open listener on the same button cannot be skipped;
			// let it no-op, then close on the next tick.
			setTimeout(() => closeBtn.click(), 0);
		});

		container.addEventListener("click", (e) => {
			if (e.target === container) closeBtn.click();
		});

		// Core drops is-menu-open the instant any close path fires, which
		// would snap the drawer away. Mirror the flip into a short-lived
		// az-closing state so the exit animation can play first.
		let wasOpen = container.classList.contains("is-menu-open");
		let closingTimer = 0;
		new MutationObserver(() => {
			const isOpen = container.classList.contains("is-menu-open");
			if (isOpen === wasOpen) return;
			wasOpen = isOpen;
			if (isOpen) {
				clearTimeout(closingTimer);
				container.classList.remove("az-closing");
			} else {
				container.classList.add("az-closing");
				closingTimer = setTimeout(() => container.classList.remove("az-closing"), 320);
			}
		}).observe(container, { attributes: true, attributeFilter: ["class"] });
	});
}

// ---- Newsletter / placeholder forms --------------------------------------------
function initPlaceholderForms() {
	document.querySelectorAll("[data-az-toast-form]").forEach((form) => {
		form.addEventListener("submit", (e) => {
			e.preventDefault();
			form.reset();
			azShowToast(form.dataset.toast || "Thanks — we'll be in touch!");
		});
	});
}

// ---- Inquiry form (real submission) -------------------------------------------
// Posts to the child theme's az-inquiry/v1/submit REST route; the wp_rest nonce
// rides in a hidden field. Outcome is toasted; delivery status is recorded
// server-side for the admin screens.
function initInquiryForm() {
	document.querySelectorAll("form[data-az-inquiry]").forEach((form) => {
		form.addEventListener("submit", async (e) => {
			e.preventDefault();

			const rest = window.aiZippy?.rest || {};
			const base = (rest.url || "/wp-json/").replace(/\/+$/, "");
			const button = form.querySelector("[type=submit]");
			const field = (name) => form.elements[name]?.value ?? "";

			button.disabled = true;

			try {
				const res = await fetch(`${base}/az-inquiry/v1/submit`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-WP-Nonce": form.elements.az_nonce?.value || rest.nonce || "",
					},
					body: JSON.stringify({
						name: field("name"),
						email: field("email"),
						phone: field("phone"),
						interest: field("interest"),
						message: field("message"),
						source: form.dataset.azInquiry || "workshop",
					}),
				});
				const json = await res.json().catch(() => ({}));

				if (res.ok && json.ok) {
					form.reset();
					azShowToast(form.dataset.toast || "Thanks — we'll be in touch!");
				} else {
					azShowToast(json.message || "Something went wrong — please try again.");
				}
			} catch {
				azShowToast("Something went wrong — please try again.");
			} finally {
				button.disabled = false;
			}
		});
	});
}

// ---- Checkout fulfillment (delivery / self-collection) -----------------------
// Server renders the pickup panel + booking fields; JS mirrors the rules for
// instant UX: pickup hides the delivery-address section and reveals the
// collection point, same-day dates disable every timeslot that can no longer
// be fulfilled today. azFulfillment is localized from
// inc/az-checkout-fulfillment.php on checkout pages only.
function initCheckoutFulfillment() {
	const cfg = window.azFulfillment;
	if (!cfg || !document.querySelector("form.checkout")) return;

	// WC replaces checkout fragments on every AJAX refresh, so re-query the
	// fields each sync and listen at body level (delegation survives swaps).
	const syncFulfillment = () => {
		const form = document.querySelector("form.checkout");
		if (!form) return;
		const chosen = form.querySelector("input[name^=shipping_method]:checked")?.value || "";
		const pickup = chosen.startsWith(cfg.pickupPrefix);
		form.classList.toggle("az-pickup", pickup);

		// Mode wording: in self-collection the Recipient card becomes the
		// Self-collection card (it holds the pickup point), and the Delivery
		// card becomes Collection with a Collection date label. Server renders
		// the session's mode; this keeps a toggle flip instant (no reload).
		if (cfg.labels) {
			const title = document.querySelector("[data-az-recipient-title]");
			if (title) title.textContent = pickup ? cfg.labels.pickupTitle : cfg.labels.recipientTitle;

			const cardTitle = document.querySelector("[data-az-delivery-title]");
			if (cardTitle) cardTitle.textContent = pickup ? cfg.labels.collectionTitle : cfg.labels.deliveryTitle;

			// The label's first node is its text (trailing nbsp included);
			// swapping only that node keeps WC's required/optional suffix.
			const dateLabel = document.querySelector('label[for="az_delivery_date"]');
			if (dateLabel?.firstChild?.nodeType === Node.TEXT_NODE) {
				dateLabel.firstChild.nodeValue =
					(pickup ? cfg.labels.collectionDateLabel : cfg.labels.deliveryDateLabel) + "\u00a0";
			}

			// The mixed-cart notice speaks in the chosen mode's words too
			// (delivery vs collection); absent on non-mixed carts.
			const notice = document.querySelector(".az-checkout__sameday-note");
			if (notice) notice.textContent = pickup ? cfg.labels.pickupNotice : cfg.labels.deliveryNotice;
		}
	};

	// Same-day timeslot eligibility, mirroring az_fulfillment_slot_eligible()
	// against the client clock: a slot is out for today unless it's one of
	// the configured same-day slots AND its window hasn't started yet (at
	// 4:08pm the 1pm–5pm slot is gone; 6pm–10pm stays). A selection that
	// becomes disabled resets to the placeholder. Server validation still
	// has the final say.
	const syncSlots = () => {
		const dateInput = document.querySelector("#az_delivery_date");
		const slotSelect = document.querySelector("#az_delivery_slot");
		if (!dateInput || !slotSelect) return;
		// Clamp manual entries below the cutoff date.
		if (dateInput.min && dateInput.value && dateInput.value < dateInput.min) {
			dateInput.value = dateInput.min;
		}
		const rules = cfg.slotRules || {};
		const sameDay = dateInput.value === rules.today;
		const now = new Date();
		// Anchor to the server's WP-timezone clock (a skewed device clock must
		// not open or close same-day slots on its own) and let it tick forward
		// from page render; fall back to the device clock when absent.
		const nowMinutes =
			Number.isFinite(rules.serverMinutes) && Number.isFinite(rules.serverEpoch)
				? rules.serverMinutes +
				  Math.floor((Date.now() - rules.serverEpoch * 1000) / 60000)
				: now.getHours() * 60 + now.getMinutes();
		slotSelect.querySelectorAll("option").forEach((opt) => {
			const slug = opt.value;
			if (!slug) return; // "Select a timeslot" placeholder
			let disabled = false;
			if (sameDay) {
				const start = parseInt(rules.slotStarts?.[slug], 10);
				disabled =
					!(rules.sameDaySlots || []).includes(slug) ||
					(Number.isFinite(start) && start <= nowMinutes);
			}
			opt.disabled = disabled;
			if (disabled && slotSelect.value === slug) slotSelect.value = "";
		});
	};

	// Top-of-page delivery / self-collection toggle (rendered by
	// az_fulfillment_mode_toggle()). The real shipping-method radios stay in
	// the sidebar — duplicated across the totals block and order review, both
	// AJAX-replaced — so the toggle only mirrors and drives them: flipping it
	// checks the matching radios everywhere (same-name group must stay
	// consistent or WC's serialization picks an arbitrary one) and fires a
	// bubbling change, which WC's checkout.js turns into update_checkout.
	const syncModeToggle = () => {
		const form = document.querySelector("form.checkout");
		if (!form) return;
		const chosen = form.querySelector("input[name^=shipping_method]:checked")?.value || "";
		const pickup = chosen.startsWith(cfg.pickupPrefix);
		document.querySelectorAll("[data-az-mode]").forEach((btn) => {
			const active = btn.dataset.azMode === "pickup" ? pickup : !!chosen && !pickup;
			btn.classList.toggle("is-active", active);
			btn.setAttribute("aria-checked", active ? "true" : "false");
		});
	};

	const syncAll = () => {
		syncFulfillment();
		syncModeToggle();
		syncSlots();
	};

	document.body.addEventListener("click", (e) => {
		const btn = e.target.closest("[data-az-mode]");
		if (!btn) return;
		const form = document.querySelector("form.checkout");
		if (!form) return;
		const wantPickup = btn.dataset.azMode === "pickup";
		const radios = [...form.querySelectorAll('input[name^="shipping_method"][type="radio"]')];
		const target = radios.find((r) => r.value.startsWith(cfg.pickupPrefix) === wantPickup);
		if (!target) return;
		radios.forEach((r) => {
			r.checked = r.value.startsWith(cfg.pickupPrefix) === wantPickup;
		});
		// Instant local feedback; the server round-trip (session mode, card
		// wording, fragments) follows via WC's change handler below.
		syncModeToggle();
		syncFulfillment();
		target.dispatchEvent(new Event("change", { bubbles: true }));
	});

	// The delivery fee depends on the chosen date (same-day vs next-day), so
	// a date change must refresh the shipping line like an address change:
	// WC's update_checkout refreshes the order-review fragment, and the
	// theme's azRefreshTotals (bound to the same event) the sidebar panel.
	let dateRefreshTimer = null;
	const refreshTotalsForDate = () => {
		clearTimeout(dateRefreshTimer);
		dateRefreshTimer = setTimeout(() => {
			window.jQuery?.(document.body).trigger("update_checkout");
		}, 500);
	};

	document.body.addEventListener("change", (e) => {
		if (e.target.matches("input[name^=shipping_method]")) {
			syncFulfillment();
			syncModeToggle();
		}
		if (e.target.matches("#az_delivery_date")) {
			syncSlots();
			refreshTotalsForDate();
		}
	});
	document.body.addEventListener("input", (e) => {
		if (e.target.matches("#az_delivery_date")) {
			syncSlots();
			refreshTotalsForDate();
		}
	});
	// WC fires updated_checkout (via jQuery on body) after fragment refreshes;
	// it also debounces a second refresh, so re-syncing here is essential.
	document.body.addEventListener("updated_checkout", syncAll);

	// The totals panel hides WC's shipping radio row (the fulfillment panel
	// above replaces it), which also hid the delivery fee now that delivery
	// costs money. Resurface it as a plain line — rate name plus price — so
	// the jump from subtotal to total is explained; self-collection keeps
	// the row hidden because there is nothing to charge.
	const decorateShippingRow = () => {
		const ul = document.querySelector("#az-checkout-totals ul#shipping_method");
		const row = ul?.closest(".az-checkout__totals-row");
		if (!row) return;
		const checked = ul.querySelector("input:checked");
		const label = checked ? ul.querySelector(`label[for="${checked.id}"]`) : null;
		const price = label?.querySelector(".woocommerce-Price-amount");
		if (!checked || !label || !price || checked.value.startsWith(cfg.pickupPrefix)) {
			row.style.display = "";
			return;
		}
		const spans = row.querySelectorAll(":scope > span");
		if (spans.length < 2) return;
		const name =
			label.firstChild?.nodeType === Node.TEXT_NODE ? label.firstChild.nodeValue : label.textContent;
		spans[0].textContent = name.replace(/:\s*$/, "").trim();
		spans[1].textContent = "";
		spans[1].appendChild(price.cloneNode(true));
		row.style.display = "flex";
	};

	// azRefreshTotals (inline checkout script) replaces the totals panel on
	// every refresh; re-decorate once the new markup is in, and once now for
	// the server-rendered panel.
	if (typeof window.azRefreshTotals === "function") {
		const refreshTotals = window.azRefreshTotals;
		window.azRefreshTotals = (cb) =>
			refreshTotals(() => {
				decorateShippingRow();
				if (typeof cb === "function") cb();
			});
	}
	decorateShippingRow();

	syncAll();
}

// ---- SHOP mega menu -----------------------------------------------------------
// The panel (#az-mega) is printed on wp_footer by inc/az-same-day.php and
// hangs under the sticky header, wired to the header's SHOP link. Desktop
// only (>=1024px): hover opens with a short grace period for crossing into
// the panel, a click on the closed panel opens it (touch/keyboard), otherwise
// the link navigates, Esc/outside-click closes. Mobile keeps the overlay
// drawer — the link navigates normally.
function initMegaMenu() {
	const panel = document.getElementById("az-mega");
	if (!panel) return;
	const link = document.querySelector('.az-nav-left a[href="/shop/"], .az-nav-left a[href$="/shop/"]');
	if (!link) return;

	const desktop = window.matchMedia("(min-width: 1024px)");
	const header = link.closest("header") || link.closest(".site-nav");
	let closeTimer = null;

	const setOpen = (next) => {
		clearTimeout(closeTimer);
		const isOpen = panel.classList.contains("is-open");
		if (next === isOpen) return;
		panel.classList.toggle("is-open", next);
		panel.setAttribute("aria-hidden", next ? "false" : "true");
		link.setAttribute("aria-expanded", next ? "true" : "false");
		if (next && header) {
			// The header is sticky, so measure it at open time — a scrolled
			// page would otherwise leave the panel floating mid-viewport.
			panel.style.top = `${header.getBoundingClientRect().bottom}px`;
		}
	};
	const openIfDesktop = () => {
		if (desktop.matches) setOpen(true);
	};
	const closeSoon = () => {
		clearTimeout(closeTimer);
		closeTimer = setTimeout(() => setOpen(false), 120);
	};

	link.setAttribute("aria-expanded", "false");
	link.addEventListener("mouseenter", openIfDesktop);
	link.addEventListener("mouseleave", closeSoon);
	link.addEventListener("focus", openIfDesktop);
	panel.addEventListener("mouseenter", () => clearTimeout(closeTimer));
	panel.addEventListener("mouseleave", closeSoon);

	link.addEventListener("click", (e) => {
		if (!desktop.matches) return;
		// A click while the panel is closed means "open it" (touch tap, or a
		// keyboard activation with no prior hover) — suppress navigation once.
		// With a mouse the panel is already hover-open by click time, so the
		// click passes through and the link goes to /shop/ as expected.
		if (panel.classList.contains("is-open")) return;
		e.preventDefault();
		setOpen(true);
	});

	document.addEventListener("click", (e) => {
		if (!panel.contains(e.target) && !link.contains(e.target)) setOpen(false);
	});
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape") setOpen(false);
	});
	// Shrinking below the breakpoint must not leave the panel stranded open.
	desktop.addEventListener("change", () => setOpen(false));
}

// ---- Admin-bar scroll flag -----------------------------------------------------
// The mobile admin bar is absolute and scrolls out of view; flag the body
// so the drawer can drop its admin-bar offset once the bar is gone.
function initScrolledFlag() {
	const update = () => document.body.classList.toggle("az-scrolled", window.scrollY > 0);
	update();
	window.addEventListener("scroll", update, { passive: true });
}

document.addEventListener("DOMContentLoaded", () => {
	initModal();
	initMemberPopup();
	initCampaignTabs();
	initGrabfood();
	initPlaceholderForms();
	initInquiryForm();
	initCheckoutFulfillment();
	initDrawer();
	initMegaMenu();
	initHeroSlider();
	initScrolledFlag();
});
